import { FC, useEffect, useState } from 'react';
import NumberInput from '../custom-input/NumberInput';
import { getAccountTankMaintenanceSettings, saveAccountTankMaintenanceSettings } from '../../api/accountTankMaintenanceApi';
import { getDefaultMaintenanceType } from '../../api/maintenanceTypesApi';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import './tankMaintenance.scss';

type AccountTankMaintenanceSettingsModalProps = {
  close: () => void;
  canEdit: boolean;
  onSaved?: () => void;
};

const AccountTankMaintenanceSettingsModal: FC<AccountTankMaintenanceSettingsModalProps> = ({
  close,
  canEdit,
  onSaved,
}): JSX.Element => {
  const [intervalDays, setIntervalDays] = useState<number>(30);
  const [reminderAdvanceDays, setReminderAdvanceDays] = useState<number>(3);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [maintenanceTypeId, setMaintenanceTypeId] = useState<number>();
  const [maintenanceTypeName, setMaintenanceTypeName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data: setting } = await getAccountTankMaintenanceSettings();
        setIntervalDays(setting.intervalDays || 30);
        setReminderAdvanceDays(setting.reminderAdvanceDays ?? 3);
        setIsEnabled(setting.isEnabled);

        let typeId = setting.maintenanceTypeId;
        let typeName = setting.maintenanceTypeName?.trim() || '';

        if (!typeId || !typeName) {
          const { data: defaultType } = await getDefaultMaintenanceType();
          typeId = typeId || defaultType.maintenanceTypeId;
          typeName = typeName || defaultType.name;
        }

        setMaintenanceTypeId(typeId);
        setMaintenanceTypeName(typeName);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isValid =
    !!maintenanceTypeId && intervalDays >= 1 && intervalDays <= 365 && reminderAdvanceDays >= 0 && reminderAdvanceDays <= 90;

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit || !isValid || !maintenanceTypeId || saving) {
      return;
    }

    setSaving(true);
    try {
      await saveAccountTankMaintenanceSettings({ maintenanceTypeId, intervalDays, reminderAdvanceDays, isEnabled });
      showToast.success(toastMessages.TANK_MAINTENANCE_SETTINGS_SAVED);
      onSaved?.();
      close();
    } catch {
      showToast.error(toastMessages.TANK_MAINTENANCE_SETTINGS_SAVE_FAIL);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tank-maintenance-modal">
      <form onSubmit={handleSave}>
        <h2>{LABEL_CONSTANTS.TANK_MAINTENANCE_SETTINGS}</h2>
        <hr />
        <p>{LABEL_CONSTANTS.TANK_MAINTENANCE_SETTINGS_HELP}</p>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="form-row">
              <label>{LABEL_CONSTANTS.MAINTENANCE_TYPE}:</label>
              <div className="input-container">
                <label>{maintenanceTypeName || '--'}</label>
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="maintenance-interval-days">{LABEL_CONSTANTS.INTERVAL_DAYS}</label>
              <div className="input-container input-container--number">
                <NumberInput
                  id="maintenance-interval-days"
                  value={intervalDays}
                  min={1}
                  max={365}
                  disabled={!canEdit}
                  required
                  onChange={e => setIntervalDays(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="maintenance-reminder-days">{LABEL_CONSTANTS.REMINDER_ADVANCE_DAYS}</label>
              <div className="input-container input-container--number">
                <NumberInput
                  id="maintenance-reminder-days"
                  value={reminderAdvanceDays}
                  min={0}
                  max={90}
                  disabled={!canEdit}
                  required
                  onChange={e => setReminderAdvanceDays(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="form-row">
              <label htmlFor="maintenance-enabled">{LABEL_CONSTANTS.ENABLED}</label>
              <div className="input-container">
                <input
                  id="maintenance-enabled"
                  type="checkbox"
                  checked={isEnabled}
                  disabled={!canEdit}
                  onChange={e => setIsEnabled(e.target.checked)}
                />
              </div>
            </div>
          </>
        )}

        <div className="flex-right submit-button tank-maintenance-modal-actions">
          <button type="button" className="small green inverted button" onClick={close}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          &emsp;
          {canEdit && (
            <button
              type="submit"
              className={isValid && !saving ? 'small green button' : 'small green button disabled'}
              disabled={!isValid || saving}>
              {BUTTON_CONSTANTS.SAVE}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default AccountTankMaintenanceSettingsModal;
